import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';
import NexumiLogo from '../common/NexumiLogo';
import { SUPPORTED_LANGUAGES, LANG_STORAGE_KEY } from '../../i18n';
import { getLandingContent } from '../auth/landingContent';
import { getPresentationContent, pageLang } from './presentationContent';
import HeroScene from './HeroScene';
import DayTimeline from './DayTimeline';
import PricingSection from './PricingSection';
import DemoRequestDialog from './DemoRequestDialog';
import RegisterCompanyDialog from './RegisterCompanyDialog';
import './presentation.css';

/**
 * Faqja publike e Nexuma-s: `/` për vizitorët pa sesion, dhe `#/prezantimi`.
 *
 * «Hyr» thërret `onLogin(email?)` nga App.js, që hap formën e kyçjes (me email-in
 * e mbushur kur vjen nga regjistrimi). Regjistrimi i kompanisë dhe çmimet janë ato
 * të landing-ut të mëparshëm; tekstet janë në shqip, anglisht dhe gjermanisht.
 *
 * Çdo veçori këtu është verifikuar në kod. Kur shtoni diçka, kontrolloni që
 * produkti e bën vërtet — mos shtoni statistika, dëshmi apo integrime.
 *
 * Navigimi brenda faqes nuk përdor `href="#seksioni"`: me HashRouter ai do ta
 * ndërronte rrugën. Lidhjet mbajnë një URL të vërtetë (`#/?s=…`), që hapet saktë
 * edhe në skedë të re, dhe klikimi vetëm rrëshqet te seksioni.
 */

const NAV = ['si-funksionon', 'prezenca', 'pushimet', 'pagat', 'cmimet'];

function readSectionParam() {
  const hash = window.location.hash || '';
  const q = hash.indexOf('?');
  return q >= 0 ? new URLSearchParams(hash.slice(q + 1)).get('s') : null;
}

/** Në header shfaqet kodi (SQ/EN/DE) që të nxë edhe navigimi gjerman; në menunë e telefonit, emri. */
function LangSelect({ id, lang, label, full = false, className = '' }) {
  const { i18n } = useTranslation();
  const pick = (e) => {
    const code = e.target.value;
    i18n.changeLanguage(code);
    try { localStorage.setItem(LANG_STORAGE_KEY, code); } catch (_) { /* privat */ }
  };
  return (
    <span className={`nxp-lang ${className}`}>
      <Globe size={16} aria-hidden="true" />
      <select id={id} value={lang} onChange={pick} aria-label={label}>
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code} aria-label={l.nativeName}>{full ? l.nativeName : l.code.toUpperCase()}</option>
        ))}
      </select>
    </span>
  );
}

export default function PresentationPage({ onLogin, basePath = '#/' }) {
  const { i18n } = useTranslation();
  const lang = pageLang(i18n.language);
  const t = getPresentationContent(lang);
  const lc = getLandingContent(lang);

  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // 'demo' | 'register' | null
  const trigger = useRef(null);

  const sectionHref = (id) => `${basePath}?s=${id}`;

  const goTo = useCallback((id, focus = true) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (focus) {
      const heading = el.querySelector('[data-section-heading]') || el;
      heading.focus({ preventScroll: true });
    }
  }, []);

  const onSectionLink = (id) => (e) => {
    e.preventDefault();
    if (!menuOpen) { goTo(id); return; }
    // Menuja e telefonit rri brenda header-it: mbyllja e saj e shkurton faqen,
    // ndaj rrëshqitja llogaritet vetëm pasi ajo të jetë mbyllur.
    setMenuOpen(false);
    requestAnimationFrame(() => requestAnimationFrame(() => goTo(id)));
  };

  useEffect(() => {
    const s = readSectionParam();
    if (s) requestAnimationFrame(() => goTo(s, false));
    else window.scrollTo(0, 0);
  }, [goTo]);

  // Stilet globale u japin `body` dhe `#root` `overflow: hidden auto`, që e prish
  // `position: sticky` (ora e «Një ditë pune»). `clip` pret njësoj horizontalisht,
  // por pa krijuar kontejner rrëshqitjeje. Vetëm sa është kjo faqe e hapur.
  useEffect(() => {
    const els = [document.body, document.getElementById('root')].filter(Boolean);
    const prev = els.map((el) => [el.style.overflowX, el.style.overflowY]);
    els.forEach((el) => { el.style.overflowX = 'clip'; el.style.overflowY = 'visible'; });
    return () => els.forEach((el, i) => { [el.style.overflowX, el.style.overflowY] = prev[i]; });
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    const prevLang = document.documentElement.lang;
    document.title = t.title;
    document.documentElement.lang = lang;
    return () => { document.title = prevTitle; document.documentElement.lang = prevLang; };
  }, [t.title, lang]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const open = (kind) => (e) => {
    trigger.current = e?.currentTarget || null;
    setMenuOpen(false);
    setDialog(kind);
  };
  const closeDialog = () => {
    setDialog(null);
    requestAnimationFrame(() => trigger.current && trigger.current.focus());
  };
  const login = () => onLogin();

  return (
    <div className="nxp-root" lang={lang}>
      <a className="nxp-skip" href={sectionHref('kryesore')} onClick={onSectionLink('kryesore')}>{t.skip}</a>

      <header className="nxp-header">
        <div className="nxp-wrap nxp-header-row">
          <a className="nxp-logo" href={basePath} onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label={t.logoLabel}>
            <NexumiLogo height={28} variant="dark" />
          </a>

          <nav className="nxp-nav" aria-label={t.navLabel}>
            {NAV.map((id) => (
              <a key={id} href={sectionHref(id)} onClick={onSectionLink(id)}>{t.nav[id]}</a>
            ))}
          </nav>

          <div className="nxp-header-actions">
            <LangSelect id="nxp-lang" lang={lang} label={t.langLabel} className="nxp-hide-sm" />
            <button type="button" className="nxp-btn nxp-btn-ghost" onClick={login}>{t.login}</button>
            <button type="button" className="nxp-btn nxp-btn-primary nxp-hide-sm" onClick={open('demo')}>{t.demo}</button>
            <button
              type="button"
              className="nxp-menu-btn"
              aria-expanded={menuOpen}
              aria-controls="nxp-mobile-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
              <span className="nxp-sr">{menuOpen ? t.menuClose : t.menuOpen}</span>
            </button>
          </div>
        </div>

        <div id="nxp-mobile-menu" className="nxp-mobile-menu" hidden={!menuOpen}>
          <nav aria-label={t.navLabelMobile}>
            {NAV.map((id) => (
              <a key={id} href={sectionHref(id)} onClick={onSectionLink(id)}>{t.nav[id]}</a>
            ))}
          </nav>
          <LangSelect id="nxp-lang-mobile" lang={lang} label={t.langLabel} full className="nxp-lang-block" />
          <button type="button" className="nxp-btn nxp-btn-primary nxp-btn-block" onClick={open('demo')}>{t.demo}</button>
          <button type="button" className="nxp-btn nxp-btn-outline nxp-btn-block" onClick={open('register')}>{t.start}</button>
        </div>
      </header>

      <main id="kryesore">
        <section className="nxp-hero" aria-labelledby="nxp-hero-title">
          <div className="nxp-wrap nxp-hero-grid">
            <div className="nxp-hero-copy">
              <h1 id="nxp-hero-title" className="nxp-h1" tabIndex={-1} data-section-heading style={{ '--nxp-h1-fit': t.hero.h1Fit }}>
                <span>{t.hero.h1a}</span>
                <span className="nxp-h1-accent">{t.hero.h1b}</span>
              </h1>
              <p className="nxp-lead">{t.hero.lead}</p>
              <div className="nxp-cta-row">
                <button type="button" className="nxp-btn nxp-btn-primary nxp-btn-lg" onClick={open('demo')}>{t.demo}</button>
                <a className="nxp-btn nxp-btn-outline nxp-btn-lg" href={sectionHref('si-funksionon')} onClick={onSectionLink('si-funksionon')}>
                  <span className="nxp-play" aria-hidden="true">
                    <svg viewBox="0 0 16 16"><path d="M6 4.5v7l5.5-3.5z" fill="currentColor" /></svg>
                  </span>
                  {t.hero.how}
                </a>
              </div>
              <p className="nxp-note">{t.hero.note}</p>
            </div>
            <HeroScene key={lang} t={t} />
          </div>
        </section>

        <DayTimeline day={t.day} onSectionLink={onSectionLink} sectionHref={sectionHref} />

        <PricingSection cm={lc.cmimet} labels={t.pricing} onStart={open('register')} onContact={open('demo')} />

        <section className="nxp-final" aria-labelledby="nxp-final-title">
          <div className="nxp-wrap nxp-final-box">
            <h2 id="nxp-final-title" className="nxp-h2">{t.finalTitle}</h2>
            <p>{t.finalDesc}</p>
            <div className="nxp-cta-row nxp-center-row">
              <button type="button" className="nxp-btn nxp-btn-light nxp-btn-lg" onClick={open('register')}>{lc.cmimet.ctaTrial}</button>
              <button type="button" className="nxp-btn nxp-btn-on-dark nxp-btn-lg" onClick={open('demo')}>{t.demo}</button>
            </div>
          </div>
        </section>
      </main>

      <footer className="nxp-footer">
        <div className="nxp-wrap nxp-footer-row">
          <NexumiLogo height={20} variant="dark" />
          <nav aria-label={t.legalLabel}>
            <a href="/privatesia/">{t.footer.privacy}</a>
            <a href="/kushtet/">{t.footer.terms}</a>
            <a href="mailto:support@nexuma.app">support@nexuma.app</a>
          </nav>
          <span className="nxp-copy">© {new Date().getFullYear()} Nexuma</span>
        </div>
      </footer>

      {dialog === 'demo' && <DemoRequestDialog t={t} lang={lang} onClose={closeDialog} />}
      {dialog === 'register' && (
        <RegisterCompanyDialog
          r={lc.register}
          closeLabel={t.demoDialog.close}
          onClose={closeDialog}
          onSignIn={(email) => { setDialog(null); onLogin(email); }}
        />
      )}
    </div>
  );
}
